import type { BaseItemContent } from "./content";
import type { BaseItemSchema } from "./item";
import type { ItemMetadata } from "./metadata";
import type { IntroScreenType, SessionType } from "./types";

export interface SessionItem extends BaseItemSchema {
	stimulus?: BaseItemContent;
	items?: BaseItemSchema[];
}

export type PartIntroContent = {
	title: string;
	partNumber?: number;
	sectionCount?: number;
	sectionsTable: Array<{
		section: number;
		sectionLabel?: string;
		itemTypes: Array<{ name: string }>;
		durationMinutes: number;
	}>;
	durationMinutes: number;
};

export type ChecklistContent = {
	checklistItems: Array<{ icon: string; text: string }>;
};

export type MicCheckContent = {
	browserCheck: { title?: string; label?: string; browserName?: string };
	microphoneCheck: {
		title?: string;
		intro?: string;
		steps?: string[];
		successMessage?: string;
		referencePhrase?: string;
	};
	requiresSuccessfulRecording?: boolean;
};

export type TestFormatContent = {
	title?: string;
	timerInfo?: string;
	approximateDurationText?: string;
	partsOverview: Array<{
		name: string;
		part: number;
		durationMinutes: number;
	}>;
};

export type ReadyToStartContent = {
	title: string;
	callouts: Array<{ icon: string; text: string }>;
};

export type BlockedSessionItems = SessionItem[][];

export type BackendSessionResponse = {
	id: number;
	numericId: number;
	collectionId: number;
	sessionItems: SessionItem[];
	errors?: {
		title: string;
		message: string;
	};
	examName: string;
	startTime: string;
	sessionType: SessionType;
	sessionSubType?: "DIAGNOSTIC" | "PRACTICE_TEST";
	totalTime: number;
	status: "ACTIVE" | "COMPLETE" | "NOT_STARTED";
};

export type ExamBreak = {
	startTime: string;
	endTime: string;
	totalBreakTimeTakenInSeconds?: number;
	status: "STARTED" | "EXPIRED";
	allotedTime: number;
	section: string;
};

export type SessionDurationSection = {
	section: number;
	timeElapsed?: number;
};

export type SessionDuration = {
	part: number;
	sections: SessionDurationSection[];
};

export type SessionResponse = {
	id: number;
	numericId: number;
	collectionId: number;
	sessionItems: BaseItemSchema[][];
	startTime?: string;
	totalTime?: number;
	examBreak?: ExamBreak[];
	examName?: string;
	sessionTime?: number;
	sessionSubType?: "DIAGNOSTIC" | "PRACTICE_TEST";
	sessionType?: SessionType;
	metadata?: {
		durations?: SessionDuration[];
	};
};

type IntroScreenItemOf<TScreen extends IntroScreenType, TContent> = Omit<
	BaseItemSchema,
	"content" | "metadata"
> & {
	content: BaseItemContent & TContent;
	metadata: ItemMetadata & { screenType: TScreen };
};

export type PartTestFormatIntroItem = IntroScreenItemOf<
	"PART_TEST_FORMAT",
	PartIntroContent
>;
export type ChecklistIntroItem = IntroScreenItemOf<
	"CHECKLIST",
	ChecklistContent
>;
export type MicCheckIntroItem = IntroScreenItemOf<"MIC_CHECK", MicCheckContent>;
export type FullTestFormatIntroItem = IntroScreenItemOf<
	"FULL_TEST_FORMAT",
	TestFormatContent
>;
export type ReadyToStartIntroItem = IntroScreenItemOf<
	"READY_TO_START",
	ReadyToStartContent
>;
